import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  model,
  output,
  signal,
  computed,
  effect,
} from '@angular/core';
import { DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { PageHeader, SectionHeader } from '@intelishs-group/ui';
import { SaldoVacaciones } from '@schemas/vacaciones.schema';
import { DiaFestivo } from '@schemas/festivos.schema';
import { VacacionesService } from '@services/vacaciones.service';
import { FestivosService } from '@services/festivos.service';
import {
  calcularDiasHabiles,
  calcularDistribucionFIFO,
  commitRangeOnEnter,
  toISODateString,
} from '@shared/vacaciones.utils';

@Component({
  selector: 'app-solicitud-nueva-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    DatePipe,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzDatePickerModule,
    NzInputModule,
    NzIconModule,
    PageHeader,
    SectionHeader,
  ],
  templateUrl: './solicitud-nueva-modal.html',
  styleUrl: './solicitud-nueva-modal.css',
})
export class SolicitudNuevaModal {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly festivosService = inject(FestivosService);
  private readonly message = inject(NzMessageService);

  visible = model(false);
  empleadoId = input<number | null>(null);
  saldo = input<SaldoVacaciones | null>(null);
  saved = output<void>();

  dateRange = signal<Date[] | null>(null);
  comentarios = signal('');
  submitting = signal(false);

  diasSolicitados = computed(() => {
    const range = this.dateRange();
    if (!range || range.length !== 2) return 0;
    return calcularDiasHabiles(range[0], range[1], this.festivosService.festivos());
  });

  distribucionFIFO = computed(() =>
    calcularDistribucionFIFO(this.saldo(), this.dateRange(), this.festivosService.festivos()),
  );

  festivosEnRango = computed((): DiaFestivo[] => {
    const range = this.dateRange();
    if (!range || range.length !== 2) return [];
    const [inicio, fin] = range;
    return this.festivosService
      .festivos()
      .filter((f) => f.fecha >= toISODateString(inicio) && f.fecha <= toISODateString(fin));
  });

  /**
   * "3 días festivos" / "1 día festivo". Los plurales se resuelven aquí y no con
   * un "día(s) festivo(s)" en la plantilla: eso es una cuenta a medio hacer, y
   * el caso de uno solo es el más común en un puente.
   */
  textoFestivos(): string {
    const n = this.festivosEnRango().length;
    return n === 1 ? '1 día festivo' : `${n} días festivos`;
  }

  festivosEnRangoNombres = computed(() =>
    this.festivosEnRango()
      .map((f) => f.nombre)
      .join(', '),
  );

  generaSaldoNegativo = computed(() => (this.distribucionFIFO()?.negativo ?? 0) > 0);
  diasNegativosResultantes = computed(() => this.distribucionFIFO()?.negativo ?? 0);

  readonly fechaMinimaSolicitud = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 14);
    return d;
  })();

  fechaInicioInvalida = computed(() => {
    const range = this.dateRange();
    if (!range || range.length < 1 || !range[0]) return false;
    const inicio = new Date(range[0]);
    inicio.setHours(0, 0, 0, 0);
    return inicio < this.fechaMinimaSolicitud;
  });

  disabledDate = (current: Date): boolean => {
    // Solo se bloquean fechas pasadas, fines de semana y festivos.
    // Las próximas 2 semanas se PERMITEN pero generan una advertencia informativa.
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const dow = current.getDay();
    return current < hoy || dow === 0 || dow === 6 || this.festivosService.esFestivo(current);
  };

  /** True para días hábiles dentro de las próximas 2 semanas (zona de advertencia). Los fines de semana quedan excluidos — ya están deshabilitados en gris. */
  esDentroDeDosSemanas = (current: Date): boolean => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const c = new Date(current);
    c.setHours(0, 0, 0, 0);
    const dow = c.getDay();
    if (dow === 0 || dow === 6) return false;
    return c >= hoy && c < this.fechaMinimaSolicitud;
  };

  readonly onRangeChange = (dates: Date[] | null): void => this.dateRange.set(dates);

  commitRangeOnEnter = commitRangeOnEnter;

  constructor() {
    // Resetear form al abrir el modal.
    effect(() => {
      if (this.visible()) {
        this.dateRange.set(null);
        this.comentarios.set('');
      }
    });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  submit(): void {
    const empId = this.empleadoId();
    const range = this.dateRange();
    if (!empId || !range || range.length !== 2) {
      this.message.warning('Selecciona el periodo de vacaciones');
      return;
    }

    // Advertencia informativa — no bloquea el envío.
    if (this.fechaInicioInvalida()) {
      this.message.warning(
        'Las vacaciones deben solicitarse con al menos 2 semanas de anticipación. ' +
          'La solicitud se enviará pero quedará marcada como fuera de política.',
        { nzDuration: 6000 },
      );
    }

    if (this.generaSaldoNegativo()) {
      this.message.warning(
        `Esta solicitud generará un saldo negativo de ${this.diasNegativosResultantes().toFixed(1)} día(s). ` +
          'Se compensará con días generados en el futuro.',
        { nzDuration: 6000 },
      );
    }

    this.submitting.set(true);
    this.vacacionesService
      .create({
        empleadoId: empId,
        fechaInicio: toISODateString(range[0]),
        fechaFin: toISODateString(range[1]),
        comentarios: this.comentarios() || undefined,
      })
      .subscribe({
        next: () => {
          this.message.success('Solicitud enviada exitosamente');
          this.submitting.set(false);
          this.saved.emit();
          this.cerrar();
        },
        error: (err) => {
          const msg = err?.error?.errorMessage ?? 'Error al crear la solicitud';
          this.message.error(msg);
          this.submitting.set(false);
        },
      });
  }
}
